import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { 
  MessageSquare, 
  MapPin, 
  Home,
  Plus,
  Quote,
  CheckCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const habitatTypes = [
  { id: 'yourte', label: 'Yourte' },
  { id: 'tiny_house', label: 'Tiny House' },
  { id: 'caravane', label: 'Caravane' },
  { id: 'cabane', label: 'Cabane' },
  { id: 'autre', label: 'Autre' }
];

export default function Testimonials() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [form, setForm] = useState({
    name: '',
    location: '',
    habitat_type: 'yourte',
    situation_summary: '',
    testimonial: '',
    outcome: '',
    anonymous: false
  });

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/testimonials`);
      setTestimonials(response.data);
    } catch (error) {
      console.error('Failed to fetch testimonials:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/testimonials`, form);
      toast.success('Merci pour votre témoignage ! Il sera publié après validation.');
      setShowAddDialog(false);
      setForm({
        name: '',
        location: '',
        habitat_type: 'yourte',
        situation_summary: '',
        testimonial: '',
        outcome: '',
        anonymous: false
      });
    } catch (error) {
      console.error('Failed to submit testimonial:', error);
      toast.error('Erreur lors de l\'envoi du témoignage');
    }
  };

  const getHabitatLabel = (type) => {
    const habitat = habitatTypes.find(h => h.id === type);
    return habitat ? habitat.label : type;
  };

  return (
    <div className="min-h-screen" data-testid="testimonials-page">
      {/* Header */}
      <div className="bg-muted/30 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">
            Témoignages
          </h1>
          <p className="text-muted-foreground">
            Des personnes comme vous partagent leur expérience face aux procédures administratives.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* CTA Banner */}
        <Card className="mb-8 bg-primary/5 border-primary/20">
          <CardContent className="py-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <MessageSquare className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold">Partagez votre expérience</h3>
                  <p className="text-sm text-muted-foreground">
                    Votre témoignage peut aider d'autres personnes dans la même situation.
                  </p>
                </div>
              </div>
              <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
                <DialogTrigger asChild>
                  <Button className="gap-2" data-testid="add-testimonial-btn">
                    <Plus className="w-4 h-4" />
                    Témoigner
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Partager mon témoignage</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="name">Votre nom ou pseudo *</Label>
                        <Input
                          id="name"
                          value={form.name}
                          onChange={(e) => setForm({ ...form, name: e.target.value })}
                          required
                          data-testid="testimonial-name-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="location">Département/Région *</Label>
                        <Input
                          id="location"
                          value={form.location}
                          onChange={(e) => setForm({ ...form, location: e.target.value })}
                          placeholder="Ex: Ardèche"
                          required
                          data-testid="testimonial-location-input"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Type d'habitat *</Label>
                      <Select value={form.habitat_type} onValueChange={(v) => setForm({ ...form, habitat_type: v })}>
                        <SelectTrigger data-testid="testimonial-habitat-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {habitatTypes.map(type => (
                            <SelectItem key={type.id} value={type.id}>{type.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="summary">Résumé de votre situation *</Label>
                      <Input
                        id="summary"
                        value={form.situation_summary}
                        onChange={(e) => setForm({ ...form, situation_summary: e.target.value })}
                        placeholder="Ex: Mise en demeure puis procès"
                        required
                        data-testid="testimonial-summary-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="testimonial">Votre témoignage *</Label>
                      <Textarea
                        id="testimonial"
                        value={form.testimonial}
                        onChange={(e) => setForm({ ...form, testimonial: e.target.value })}
                        placeholder="Racontez votre expérience, ce qui vous a aidé, vos conseils..."
                        rows={5}
                        required
                        data-testid="testimonial-text-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="outcome">Issue de votre situation (optionnel)</Label>
                      <Input
                        id="outcome"
                        value={form.outcome}
                        onChange={(e) => setForm({ ...form, outcome: e.target.value })}
                        placeholder="Ex: Régularisation obtenue"
                        data-testid="testimonial-outcome-input"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        id="anonymous"
                        checked={form.anonymous}
                        onCheckedChange={(checked) => setForm({ ...form, anonymous: checked })}
                      />
                      <Label htmlFor="anonymous" className="text-sm cursor-pointer">
                        Publier anonymement (seuls le département et le type d'habitat seront visibles)
                      </Label>
                    </div>
                    <Button type="submit" className="w-full" data-testid="submit-testimonial-btn">
                      Envoyer mon témoignage
                    </Button>
                    <p className="text-xs text-muted-foreground text-center">
                      Votre témoignage sera vérifié avant publication.
                    </p>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </CardContent>
        </Card>

        {/* Testimonials List */}
        {loading ? (
          <div className="space-y-6">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="py-6">
                  <div className="h-4 bg-muted rounded w-3/4 mb-4" />
                  <div className="h-20 bg-muted rounded" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : testimonials.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <MessageSquare className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">Aucun témoignage pour le moment</h3>
              <p className="text-muted-foreground mb-4">
                Soyez le premier à partager votre expérience !
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="overflow-hidden" data-testid={`testimonial-${testimonial.id}`}>
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <Quote className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className="font-semibold">
                          {testimonial.anonymous ? 'Anonyme' : testimonial.name}
                        </span>
                        <Badge variant="outline" className="gap-1">
                          <MapPin className="w-3 h-3" />
                          {testimonial.location}
                        </Badge>
                        <Badge variant="secondary" className="gap-1">
                          <Home className="w-3 h-3" />
                          {getHabitatLabel(testimonial.habitat_type)}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">
                        {testimonial.situation_summary}
                      </p>
                      <blockquote className="border-l-2 border-primary pl-4 italic text-muted-foreground">
                        "{testimonial.testimonial}"
                      </blockquote>
                      {testimonial.outcome && (
                        <div className="mt-4 flex items-center gap-2 text-sm text-green-600">
                          <CheckCircle className="w-4 h-4" />
                          <span className="font-medium">{testimonial.outcome}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Bottom CTA */}
        <Card className="mt-8 bg-accent/30">
          <CardContent className="py-6 text-center">
            <h3 className="font-semibold mb-2">Vous êtes dans une situation similaire ?</h3>
            <p className="text-muted-foreground mb-4">
              Inscrivez-vous sur la carte pour recevoir du soutien de la communauté.
            </p>
            <Button asChild>
              <a href="/carte">M'inscrire sur la carte</a>
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
